import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RoundedBtn } from "../../components/button.jsx/Btn";
import { SuccessModal } from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import { AVAILABLE_MODULES } from "../../data/modules";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const NEW_PERMISSION_POSITIONS_CACHE_KEY = "cehr_new_permission_positions_v1";

export default function PermissionsForm() {
  const [step, setStep] = useState(1);
  const cachedData = useMemo(
    () =>
      readSessionCache(NEW_PERMISSION_POSITIONS_CACHE_KEY, { positions: [] }),
    [],
  );
  const [positions, setPositions] = useState(cachedData?.positions || []);
  const [isLoading, setIsLoading] = useState(!cachedData?.positions?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const apiBaseUrl = getApiBaseUrl();
  const preselectedPositionId =
    location.state?.positionId ||
    new URLSearchParams(location.search).get("positionId") ||
    "";

  const [formData, setFormData] = useState({
    positionId: "",
    permissionName: "",
    description: "",
    components: [],
  });

  useEffect(() => {
    if (preselectedPositionId) {
      setFormData((prev) => ({
        ...prev,
        positionId: preselectedPositionId,
      }));
    }
  }, [preselectedPositionId]);

  const loadPositions = async () => {
    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    try {
      if (positions.length === 0) {
        setIsLoading(true);
      }
      const response = await axios.get(`${apiBaseUrl}/organizations/positions`);
      const nextPositions = response?.data?.positions || [];
      setPositions(nextPositions);
      writeSessionCache(NEW_PERMISSION_POSITIONS_CACHE_KEY, {
        positions: nextPositions,
      });
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to load positions. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Load positions on mount and refresh silently on focus.
  useEffect(() => {
    loadPositions();
  }, []);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadPositions();
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [positions.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrorMessage("");
  };

  const toggleComponentSelection = (componentId) => {
    setFormData((prev) => ({
      ...prev,
      components: prev.components.includes(componentId)
        ? prev.components.filter((c) => c !== componentId)
        : [...prev.components, componentId],
    }));
  };

  const handleProceedToStep2 = () => {
    if (!formData.positionId) {
      setErrorMessage("Please select a position.");
      return;
    }
    if (!formData.permissionName.trim()) {
      setErrorMessage("Permission name is required.");
      return;
    }
    if (!formData.description.trim()) {
      setErrorMessage("Description is required.");
      return;
    }
    if (formData.components.length === 0) {
      setErrorMessage("Please select at least one module/component.");
      return;
    }
    setErrorMessage("");
    setStep(2);
  };

  const getSelectedModuleLabels = () => {
    return formData.components
      .map((id) => AVAILABLE_MODULES.find((m) => m.id === id)?.label || id)
      .join(", ");
  };

  const handleSubmit = async () => {
    setErrorMessage("");

    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `${apiBaseUrl}/organizations/permissions`,
        formData,
      );

      if (response.data && response.data.permission) {
        setSuccessMessage("Permission created successfully");
        setOpenSuccessModal(true);
        setTimeout(() => {
          navigate("/company-settings/permissions");
        }, 2000);
      }
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create permission. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPositionName = (positionId) => {
    const position = positions.find((p) => p.id === positionId);
    return position ? position.positionName : "Unknown Position";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading positions...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-Nunito p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Permissions", path: "/company-settings/permissions" },
          { label: "Create Permission" },
        ]}
        title="Create Permission"
        subtitle="Set up a new permission set for a position."
        fallbackPath="/company-settings/permissions"
        backLabel="Back to Permissions"
      />

      <div className="p-8 w-full max-w-7xl">
        {/* Step Indicator */}
        <div className="flex items-center space-x-6 mb-8">
          <div
            className={`flex items-center ${
              step === 1 ? "text-[#1234CD]" : "text-gray-400"
            }`}
          >
            <div
              className={`w-6 h-6 flex items-center justify-center rounded-full border ${
                step === 1 ? "bg-[#1234CD] text-white" : "bg-gray-200"
              }`}
            >
              1
            </div>
            <span className="ml-2">Permission Details</span>
          </div>
          <div
            className={`flex items-center ${
              step === 2 ? "text-[#1234CD]" : "text-gray-400"
            }`}
          >
            <div
              className={`w-6 h-6 flex items-center justify-center rounded-full border ${
                step === 2 ? "bg-[#1234CD] text-white" : "bg-gray-200"
              }`}
            >
              2
            </div>
            <span className="ml-2">Review & Create</span>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
            {errorMessage}
          </div>
        )}

        {/* Step 1: Permission Details */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Position <span className="text-red-500">*</span>
              </label>
              <select
                name="positionId"
                value={formData.positionId}
                onChange={handleInputChange}
                className="w-full p-3 border border-[#D0D0D0] rounded-lg text-[#030303] focus:ring-blue-400"
              >
                <option value="">Select a position</option>
                {positions.map((position) => (
                  <option
                    key={position.id}
                    value={position.id}
                  >
                    {position.positionName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Permission Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="permissionName"
                placeholder="E.g., View Reports"
                value={formData.permissionName}
                onChange={handleInputChange}
                className="w-full p-3 border border-[#D0D0D0] rounded-lg text-[#030303] focus:ring-blue-400"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Select Modules/Components{" "}
                <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">
                Select all modules this position should have access to:
              </p>
              <div className="grid grid-cols-2 gap-3 p-4 border border-[#D0D0D0] rounded-lg bg-white max-h-96 overflow-y-auto">
                {AVAILABLE_MODULES.map((module) => (
                  <label
                    key={module.id}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.components.includes(module.id)}
                      onChange={() => toggleComponentSelection(module.id)}
                      className="w-4 h-4 text-[#1234CD] rounded focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">
                      {module.label}
                    </span>
                  </label>
                ))}
              </div>
              {formData.components.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-[#1234CD]">
                  Selected: {formData.components.length} module(s)
                </div>
              )}
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                placeholder="Enter a detailed description of this permission"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                className="w-full p-3 border border-[#D0D0D0] rounded-lg text-[#030303] focus:ring-blue-400"
              />
            </div>

            <div className="mt-48 flex justify-center items-center">
              <RoundedBtn onClick={handleProceedToStep2}>
                Save and continue
              </RoundedBtn>
            </div>
          </div>
        )}

        {/* Step 2: Review & Create */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">
                Review Permission Details
              </h3>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Position</p>
                  <p className="text-base font-medium">
                    {getPositionName(formData.positionId)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Permission Name</p>
                  <p className="text-base font-medium">
                    {formData.permissionName}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Modules/Components</p>
                  <div className="text-base font-medium space-y-1">
                    {formData.components.map((componentId) => (
                      <div
                        key={componentId}
                        className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2 mb-1"
                      >
                        {AVAILABLE_MODULES.find((m) => m.id === componentId)
                          ?.label || componentId}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Description</p>
                  <p className="text-base">{formData.description}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-8 items-center mt-40">
              <button
                onClick={() => setStep(1)}
                disabled={isSubmitting}
                className="w-40 border-[#5570F1] border-2 px-4 py-3 rounded-lg text-[#5570F1] font-medium hover:bg-blue-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#1234CD] w-40 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        )}
      </div>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        title="Success!"
        message={successMessage}
      />
    </div>
  );
}
